/**
 * German label maps for the enum unions in `lib/types.ts` (PHASE1 §7.4).
 *
 * The whole UI is German-only; every enum value shown to the user resolves
 * through one of these maps. `opsForField` returns the operators a rule
 * condition may use for a given field (text vs. amount).
 */
import type {
  AccountType,
  BankProfile,
  ConnectionStatus,
  ContractAlert,
  ContractCadence,
  DedupWire,
  EbEnvironment,
  EmailMatchStatus,
  EmailOutcome,
  EmailParseStatus,
  GroupCadence,
  GroupConfidence,
  Provider,
  ReviewStatus,
  RuleField,
  RuleOperator,
  SyncAccountStatus,
  SyncRunStatus,
  SyncTrigger,
  TransactionSource,
  TransactionStatus,
  TransferConfidence,
} from './types.ts';

export const ACCOUNT_TYPE: Record<AccountType, string> = {
  checking: 'Girokonto',
  savings: 'Sparkonto',
  credit_card: 'Kreditkarte',
  cash: 'Bargeld',
};

export const REVIEW_STATUS: Record<ReviewStatus, string> = {
  pending: 'Offen',
  reviewed: 'Kategorisiert',
  skipped: 'Übersprungen',
};

export const STATUS: Record<TransactionStatus, string> = {
  booked: 'Gebucht',
  pending: 'Vorgemerkt',
};

export const SOURCE: Record<TransactionSource, string> = {
  manual: 'Manuell',
  file_import: 'Datei-Import',
  enablebanking: 'Bankverbindung',
  fints: 'FinTS',
  email: 'E-Mail (Kontowecker)',
};

export const DEDUP: Record<DedupWire, string> = {
  new: 'Neu',
  duplicate: 'Duplikat',
  possible_duplicate: 'Mögliches Duplikat',
  ignored: 'Ignoriert',
};

// ── Inbox groups ("Serien") ─────────────────────────────────────────────────

/** `InboxGroup.cadence` → the `GroupBanner` header suffix. */
export const GROUP_CADENCE: Record<GroupCadence, string> = {
  weekly: 'wöchentlich',
  monthly: 'monatlich',
  quarterly: 'vierteljährlich',
  yearly: 'jährlich',
};

/** `InboxGroup.confidence` — a group never reaches `'low'` (see `confidence_of`). */
export const GROUP_CONFIDENCE: Record<GroupConfidence, string> = {
  high: 'Sehr sicher',
  medium: 'Wahrscheinlich',
};

// ── Verträge (Phase 6) ──────────────────────────────────────────────────────

export const CONTRACT_CADENCE: Record<ContractCadence, string> = {
  weekly: 'wöchentlich',
  monthly: 'monatlich',
  quarterly: 'vierteljährlich',
  half_yearly: 'halbjährlich',
  yearly: 'jährlich',
};

export const CONTRACT_ALERT: Record<ContractAlert, string> = {
  overdue: 'überfällig',
  amount_drift: 'Betrag weicht ab',
};

// ── E-Mail-Import ("Kontowecker", Phase 7) ──────────────────────────────────

export const EMAIL_PARSE_STATUS: Record<EmailParseStatus, string> = {
  pending: 'Ausstehend',
  parsed: 'Geparst',
  ignored: 'Ignoriert',
  error: 'Fehler',
};

export const EMAIL_OUTCOME: Record<EmailOutcome, string> = {
  created_pending: 'Vorgemerkt angelegt',
  enriched_existing: 'Buchung angereichert',
  consumed_into_booked: 'In Buchung übernommen',
};

export const EMAIL_MATCH_STATUS: Record<EmailMatchStatus, string> = {
  unmatched: 'Kein Treffer',
  matched: 'Zugeordnet',
  ambiguous: 'Mehrdeutig',
};

export const CONDITION_FIELD: Record<RuleField, string> = {
  payee: 'Empfänger',
  purpose: 'Verwendungszweck',
  iban: 'IBAN',
  amount: 'Betrag',
};

export const CONDITION_OP: Record<RuleOperator, string> = {
  contains: 'enthält',
  equals: 'ist gleich',
  starts_with: 'beginnt mit',
  ends_with: 'endet mit',
  gt: 'größer als',
  gte: 'größer oder gleich',
  lt: 'kleiner als',
  lte: 'kleiner oder gleich',
  abs_equals: 'Betrag (ohne Vorzeichen) gleich',
};

export const BANK_PROFILE: Record<BankProfile, string> = {
  camt053: 'CAMT.053 (XML)',
  haspa_csv: 'Sparkasse / Haspa (CSV)',
  commerzbank_csv: 'Commerzbank (CSV)',
  dkb_csv: 'DKB (CSV)',
};

// ── Enable Banking (Phase 2, §7.5) ──────────────────────────────────────────

/** `bank_connections.status` → the badge label in `StatusBadge`. */
export const CONNECTION_STATUS: Record<ConnectionStatus, string> = {
  pending_auth: 'Wartet',
  active: 'Aktiv',
  expired: 'Abgelaufen',
  revoked: 'Getrennt',
  error: 'Fehler',
};

/** `sync_runs.trigger` → the trigger badge in the sync-health panel. */
export const SYNC_TRIGGER: Record<SyncTrigger, string> = {
  scheduled: 'Geplant',
  manual: 'Manuell',
  bootstrap: 'Erstimport',
};

/** `sync_runs.status` roll-up label. */
export const SYNC_RUN_STATUS: Record<SyncRunStatus, string> = {
  running: 'Läuft',
  success: 'Erfolgreich',
  partial: 'Teilweise',
  error: 'Fehlgeschlagen',
};

/**
 * `sync_runs.status` → the toast sentence shown after a sync finishes
 * (`ConnectionCard`, both Enable-Banking and FinTS). `running` is never
 * passed in practice (only a finished `run` reaches the toast); it mirrors
 * the failure sentence, matching the previous inline fallback arm.
 */
export const SYNC_RUN_SENTENCE: Record<SyncRunStatus, string> = {
  running: 'Abgleich fehlgeschlagen.',
  success: 'Abgleich abgeschlossen.',
  partial: 'Abgleich mit Teilfehlern abgeschlossen.',
  error: 'Abgleich fehlgeschlagen.',
};

/** Per-account sync outcome label. */
export const SYNC_ACCOUNT_STATUS: Record<SyncAccountStatus, string> = {
  ok: 'OK',
  error: 'Fehler',
  skipped: 'Übersprungen',
};

/** EB app environment label. */
export const EB_ENVIRONMENT: Record<EbEnvironment, string> = {
  sandbox: 'Sandbox',
  production: 'Produktion',
};

/** `bank_connections.provider` → the channel label shown on connection surfaces
 * (Phase 4, §7.3). `manual` is unused by any connection today but kept total. */
export const PROVIDER: Record<Provider, string> = {
  enablebanking: 'Enable Banking',
  fints: 'FinTS',
  manual: 'Manuell',
};

/** Account provenance: "FinTS · Haspa", "Enable Banking · Haspa", or "Manuell" —
 * which connection/channel feeds this account (Konten page chip + merge dialog). */
export function accountChannelLabel(account: {
  connection_provider: Provider | null;
  connection_label: string | null;
}): string {
  if (account.connection_provider == null) return PROVIDER.manual;
  const channel = PROVIDER[account.connection_provider];
  return account.connection_label ? `${channel} · ${account.connection_label}` : channel;
}

/**
 * The four states an account can be in, in the user's vocabulary. Three flags
 * back them (`archived`, `hidden`, `removed`) but only ONE state is ever shown,
 * so the order below is the precedence: a wiped tombstone is written
 * `hidden + archived + removed` and must read as „Entfernt", never „Geschlossen".
 *
 * „Geschlossen" (= the `archived` flag) is deliberately not called „archiviert"
 * any more: it stops the sync AND drops the account out of the Nettovermögen,
 * while its past bookings keep counting in Berichte/Budgets — which is what
 * closing a real bank account means. Category archiving is a different feature
 * and keeps its own wording.
 */
export type AccountLifecycleState = 'active' | 'closed' | 'hidden' | 'removed';

export function accountLifecycleState(account: {
  archived: boolean;
  hidden: boolean;
  removed: boolean;
}): AccountLifecycleState {
  if (account.removed) return 'removed';
  if (account.hidden) return 'hidden';
  if (account.archived) return 'closed';
  return 'active';
}

/** State → badge text on the Konten page. */
export const ACCOUNT_LIFECYCLE: Record<AccountLifecycleState, string> = {
  active: 'Aktiv',
  closed: 'Geschlossen',
  hidden: 'Ausgeblendet',
  removed: 'Entfernt',
};

/** State → the one-line explanation under the badge in „Inaktive Konten". */
export const ACCOUNT_LIFECYCLE_HINT: Record<AccountLifecycleState, string> = {
  active: '',
  closed: 'Kein Abgleich, zählt nicht zum Nettovermögen. Buchungen zählen weiter in Berichten.',
  hidden: 'Konto und Buchungen sind aus allen Auswertungen ausgeblendet.',
  removed: 'Daten gelöscht. Das Konto kommt auch bei einer neuen Verbindung nicht zurück.',
};

// ── Transfers (Phase 3, §4.4) ───────────────────────────────────────────────

/** `TransferCandidateOut.confidence` → the confidence chip label. */
export const TRANSFER_CONFIDENCE: Record<TransferConfidence, string> = {
  high: 'Sehr sicher',
  medium: 'Wahrscheinlich',
  low: 'Unsicher',
};

// ── Receipts ("Belege") ─────────────────────────────────────────────────────

/** `ReceiptCandidate.confidence` → the candidate-match chip label. */
export const RECEIPT_CONFIDENCE: Record<'high' | 'medium' | 'low', string> = {
  high: 'Sicher',
  medium: 'Wahrscheinlich',
  low: 'Unsicher',
};

const TEXT_OPS: RuleOperator[] = ['contains', 'equals', 'starts_with', 'ends_with'];
const AMOUNT_OPS: RuleOperator[] = ['equals', 'gt', 'gte', 'lt', 'lte', 'abs_equals'];

/** Operators valid for a given rule-condition field (§5 evaluation contract). */
export function opsForField(field: RuleField): RuleOperator[] {
  return field === 'amount' ? AMOUNT_OPS : TEXT_OPS;
}
